// STARWEAVE — flight controls. Dual-stick touch: a steering joystick (right
// thumb: drag to pitch/yaw, springs back to centre = no turn) and a throttle
// slider (left thumb: drag up for speed, all the way down = hover/stop). Direct
// and responsive — NO auto-drift, NO auto-pilot. You fly around one real planet
// and can settle onto its surface. Keyboard (W/S throttle, arrows steer, H warp)
// for desktop testing. Forward = +X (ship local).

import * as THREE from 'three';
import type { ShipBuild } from './ship';
import { KM_PER_UNIT, type Body } from './starsystem';

export interface FlightNav {
  speedKms: number; nearest: string; distKm: number; system: number;
  throttle: number; grounded: boolean;
}
export interface FlightRig {
  update(now: number, dt: number, camera: THREE.PerspectiveCamera): FlightNav;
}

const MAX_SPEED = 170;   // units/sec at full throttle
const TURN_RATE = 1.3;   // rad/sec at full stick deflection

function dotTexture(): THREE.Texture {
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

export function createFlight(
  scene: THREE.Scene, ship: ShipBuild, bodies: Body[], R: number,
  onWarp: (index: number) => Body[] | null,
): FlightRig {
  // ---- Engine trail (kept) -------------------------------------------------
  const TRAIL = 90;
  const tp = new Float32Array(TRAIL * 3); tp.fill(1e7);
  const tc = new Float32Array(TRAIL * 3);
  const accent = new THREE.Color(ship.accent);
  for (let i = 0; i < TRAIL; i++) {
    const f = Math.pow(1 - i / TRAIL, 1.5) * (0.45 + 0.4 * Math.min(1, i / 6));
    tc[i * 3] = accent.r * f; tc[i * 3 + 1] = accent.g * f; tc[i * 3 + 2] = accent.b * f;
  }
  const tgeo = new THREE.BufferGeometry();
  tgeo.setAttribute('position', new THREE.BufferAttribute(tp, 3));
  tgeo.setAttribute('color', new THREE.BufferAttribute(tc, 3));
  const trail = new THREE.Points(tgeo, new THREE.PointsMaterial({
    size: 3.4, map: dotTexture(), vertexColors: true, transparent: true, opacity: 0.85,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  }));
  trail.frustumCulled = false;
  scene.add(trail);

  // ---- State ---------------------------------------------------------------
  const X = new THREE.Vector3(1, 0, 0);
  const Y = new THREE.Vector3(0, 1, 0);
  const planet = bodies[0];                 // the world we fly around (at origin)
  const groundR = planet.radius * 1.04;     // collision/landing shell
  // Start up-and-out with the world filling the view ahead, nose aimed at it so
  // throttling up flies you down toward the surface.
  const shipPos = new THREE.Vector3(planet.radius * 1.0, planet.radius * 1.6, planet.radius * 2.4);
  const heading = new THREE.Quaternion().setFromUnitVectors(
    X, new THREE.Vector3(0, planet.radius * 0.2, 0).sub(shipPos).normalize());
  const forward = new THREE.Vector3();
  const upv = new THREE.Vector3();
  const ep = new THREE.Vector3();
  const camPos = new THREE.Vector3();
  const lookAt = new THREE.Vector3();
  const qStep = new THREE.Quaternion();
  const eStep = new THREE.Euler();
  const qbank = new THREE.Quaternion();

  // ---- Input: steer vector [-1..1], throttle [0..1] ------------------------
  let steerX = 0, steerY = 0;   // right stick: x=yaw, y=pitch
  let throttle = 0;
  let warpRequested = false;
  const keys = new Set<string>();

  // On-screen controls (DOM), themed to index.html.
  const A = '#ffb340';
  const mk = (css: string) => { const d = document.createElement('div'); d.style.cssText = css; document.body.appendChild(d); return d; };
  const stickBase = mk(`position:fixed;right:26px;bottom:32px;width:132px;height:132px;border-radius:50%;
    border:1.5px solid rgba(255,179,64,.5);background:radial-gradient(circle,rgba(20,30,46,.34),rgba(10,16,28,.5));
    z-index:30;touch-action:none;backdrop-filter:blur(3px)`);
  const stickKnob = mk(`position:fixed;right:62px;bottom:68px;width:60px;height:60px;border-radius:50%;
    background:radial-gradient(circle at 40% 35%, #ffd98a, ${A});box-shadow:0 0 16px rgba(255,179,64,.5);
    z-index:31;pointer-events:none;transition:transform .04s linear`);
  const throttleTrack = mk(`position:fixed;left:26px;bottom:32px;width:54px;height:200px;border-radius:27px;
    border:1.5px solid rgba(255,179,64,.5);background:linear-gradient(0deg,rgba(255,179,64,.18),rgba(10,16,28,.5));
    z-index:30;touch-action:none;overflow:hidden;backdrop-filter:blur(3px)`);
  const throttleFill = mk(`position:fixed;left:26px;bottom:32px;width:54px;height:0px;border-radius:0 0 27px 27px;
    background:linear-gradient(0deg, ${A}, rgba(255,179,64,.35));z-index:30;pointer-events:none`);
  const throttleLbl = mk(`position:fixed;left:26px;bottom:238px;width:54px;text-align:center;color:${A};
    font:600 11px 'Avenir Next Condensed',sans-serif;letter-spacing:1px;z-index:31;pointer-events:none`);
  throttleLbl.textContent = 'THRUST';

  const TRACK_H = 200;
  const renderControls = () => {
    stickKnob.style.transform = `translate(${steerX * 36}px, ${steerY * 36}px)`;
    const h = Math.round(throttle * TRACK_H);
    throttleFill.style.height = `${h}px`;
  };
  renderControls();

  // Pointer routing: right half = steer stick (floating origin at stick base),
  // left-edge track = throttle. Multi-touch via pointerId.
  const stickRect = () => stickBase.getBoundingClientRect();
  const trackRect = () => throttleTrack.getBoundingClientRect();
  let steerPtr = -1, thrPtr = -1;
  const setSteerFrom = (x: number, y: number) => {
    const r = stickRect(); const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    steerX = THREE.MathUtils.clamp((x - cx) / 56, -1, 1);
    steerY = THREE.MathUtils.clamp((y - cy) / 56, -1, 1);
  };
  const setThrFrom = (y: number) => {
    const r = trackRect();
    throttle = THREE.MathUtils.clamp((r.bottom - y) / r.height, 0, 1);
  };
  const onDown = (e: PointerEvent) => {
    const inTrack = (() => { const r = trackRect(); return e.clientX >= r.left - 20 && e.clientX <= r.right + 20 && e.clientY >= r.top - 20 && e.clientY <= r.bottom + 20; })();
    if (inTrack && thrPtr < 0) { thrPtr = e.pointerId; setThrFrom(e.clientY); }
    else if (e.clientX > window.innerWidth * 0.5 && steerPtr < 0) { steerPtr = e.pointerId; setSteerFrom(e.clientX, e.clientY); }
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId === steerPtr) setSteerFrom(e.clientX, e.clientY);
    else if (e.pointerId === thrPtr) setThrFrom(e.clientY);
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId === steerPtr) { steerPtr = -1; steerX = 0; steerY = 0; }   // spring back
    if (e.pointerId === thrPtr) thrPtr = -1;                                    // throttle holds
  };
  addEventListener('pointerdown', onDown);
  addEventListener('pointermove', onMove);
  addEventListener('pointerup', onUp);
  addEventListener('pointercancel', onUp);
  addEventListener('keydown', (e) => { keys.add(e.key.toLowerCase()); if (e.key.toLowerCase() === 'h') warpRequested = true; });
  addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));

  // Warp button (top-centre) — jump to a fresh world.
  const warpBtn = mk(`position:fixed;left:50%;top:14px;transform:translateX(-50%);z-index:31;
    padding:7px 16px;border:1px solid rgba(255,179,64,.55);border-radius:16px;color:${A};
    background:rgba(10,16,28,.5);font:600 12px 'Avenir Next Condensed',sans-serif;letter-spacing:2px;
    backdrop-filter:blur(4px);cursor:pointer;touch-action:manipulation`);
  warpBtn.textContent = 'WARP';
  warpBtn.addEventListener('pointerdown', (e) => { e.stopPropagation(); warpRequested = true; });

  let activeBodies = bodies;
  let systemIndex = 0;
  let started = false;
  let grounded = false;

  const flashEl = mk(`position:fixed;inset:0;pointer-events:none;z-index:40;opacity:0;background:radial-gradient(circle at 50% 50%, #eaf6ff, #7fbcff);mix-blend-mode:screen`);
  const doFlash = () => {
    flashEl.style.transition = 'none'; flashEl.style.opacity = '0.92';
    requestAnimationFrame(() => { flashEl.style.transition = 'opacity .7s ease-out'; flashEl.style.opacity = '0'; });
  };

  return {
    update(now, dt, camera) {
      // keyboard fallback (desktop)
      let kx = 0, ky = 0;
      if (keys.has('arrowleft') || keys.has('a')) kx -= 1;
      if (keys.has('arrowright') || keys.has('d')) kx += 1;
      if (keys.has('arrowup')) ky -= 1;
      if (keys.has('arrowdown')) ky += 1;
      if (keys.has('w')) throttle = THREE.MathUtils.clamp(throttle + dt * 0.8, 0, 1);
      if (keys.has('s')) throttle = THREE.MathUtils.clamp(throttle - dt * 0.8, 0, 1);
      const sx = steerX || kx, sy = steerY || ky;

      // Apply steering directly to heading (yaw about local up, pitch about local right).
      eStep.set(-sy * TURN_RATE * dt, -sx * TURN_RATE * dt, 0, 'YXZ');
      qStep.setFromEuler(eStep);
      heading.multiply(qStep).normalize();

      forward.copy(X).applyQuaternion(heading);
      upv.copy(Y).applyQuaternion(heading);
      const speed = throttle * MAX_SPEED;
      shipPos.addScaledVector(forward, speed * dt);

      // Ground: don't sink through the planet. Settle onto the surface = landed.
      const alt = shipPos.length() - groundR;
      if (alt < 0) {
        shipPos.setLength(groundR);
        grounded = throttle < 0.06;
      } else grounded = false;

      // Warp to a fresh world.
      if (warpRequested) {
        warpRequested = false;
        systemIndex++;
        const nb = onWarp(systemIndex);
        if (nb) activeBodies = nb;
        shipPos.set(planet.radius * 1.0, planet.radius * 1.6, planet.radius * 2.4);
        heading.setFromUnitVectors(X, new THREE.Vector3(0, planet.radius * 0.2, 0).sub(shipPos).normalize());
        throttle = 0; tp.fill(1e7); started = false; doFlash();
      }

      // Ship transform with a visual bank into the yaw.
      qbank.setFromAxisAngle(X, -sx * 0.5);
      ship.group.position.copy(shipPos);
      ship.group.quaternion.copy(heading).multiply(qbank);

      // Engine trail.
      ep.set(-6.5, 0, 0).applyQuaternion(ship.group.quaternion).multiplyScalar(ship.group.scale.x).add(shipPos);
      tp.copyWithin(3, 0, (TRAIL - 1) * 3);
      tp[0] = ep.x; tp[1] = ep.y; tp[2] = ep.z;
      tgeo.attributes.position.needsUpdate = true;

      // Chase camera (a touch above + behind, banks with the ship).
      camPos.copy(shipPos).addScaledVector(forward, -26).addScaledVector(upv, 9);
      lookAt.copy(shipPos).addScaledVector(forward, 14);
      if (!started) { camera.position.copy(camPos); started = true; }
      else camera.position.lerp(camPos, 0.12);
      camera.up.copy(upv);
      camera.lookAt(lookAt);

      renderControls();

      // Nearest body for nav (just the world here).
      let nName = activeBodies[0].name, nDist = Infinity;
      for (const b of activeBodies) { const d = shipPos.distanceTo(b.pos) - b.radius; if (d < nDist) { nDist = d; nName = b.name; } }
      return {
        speedKms: Math.round(speed * KM_PER_UNIT), nearest: nName,
        distKm: Math.round(Math.max(0, alt) * KM_PER_UNIT), system: systemIndex,
        throttle, grounded,
      };
    },
  };
}
